import React, { useEffect } from 'react';
import { Field, useFormikContext } from 'formik';

import { AutoCompleteSelectUI, DatePickerUI, FormErrorUI, FormLabelUI, InputUI, TimePickerUi } from '../Interface';
import dayjs from 'dayjs';
import useUsers from '../../context/UsersContext';
import useParam from '../../context/ParamContext';

const FormFields = ({ autocompleteOptions, getTaskListByLineId, autoCompleteData, viewField, data }) => {
  const { user } = useUsers();
  const { params } = useParam();
  const { values, validateField, setFieldValue, errors } = useFormikContext();

  useEffect(() => {
    if (values.projectId) {
      getTaskListByLineId(values.projectId);
    }
  }, [values.projectId]);

  return (
    <div className="grid grid-cols-4 gap-x-4 gap-y-4 md:mx-4 mb-5">
      <div className="col-span-4 md:col-span-2">
        <Field
          type="autocomplete"
          name="projectId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Projekt" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.projectId} />}
          onChange={(_e, newVal) => {
            setFieldValue('projectId', newVal?.value ?? null).then(() => {
              validateField('projectId');
            });
          }}
          InputLabelProps={{ shrink: values.projectId !== '' }}
          selectedValue={values.projectId}
          selectedLabelValue={values?.project?.projectName}
          dataset={autocompleteOptions.project}
          disabled={viewField}
        />
      </div>
      <div className="col-span-4 md:col-span-2">
        <Field
          type="autocomplete"
          name="taskListId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Feladat" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.taskListId} />}
          onChange={(_e, newVal) => {
            const foundTaskList = autoCompleteData.taskLists.find((taskList) => taskList.id === newVal?.value);
            setFieldValue('statusId', foundTaskList?.statusId ?? null);
            setFieldValue('preparedness', foundTaskList?.status?.preparedness ?? null);
            setFieldValue('taskListId', newVal?.value ?? null).then(() => {
              validateField('taskListId');
            });
          }}
          InputLabelProps={{ shrink: values.taskListId !== '' }}
          disabled={!values.projectId || viewField}
          selectedValue={autocompleteOptions.taskLists?.length ? values.taskListId : null}
          selectedLabelValue={
            autocompleteOptions.taskLists?.length
              ? autocompleteOptions.taskLists.find((opt) => opt.value === values.taskListId)?.label ?? ''
              : ''
          }
          dataset={autocompleteOptions.taskLists}
        />
      </div>
      <div className="col-span-4 md:col-span-2">
        <Field
          type="date"
          name="date"
          component={DatePickerUI}
          value={values.date}
          fullWidth
          label={<FormLabelUI text="Dátum" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.date} />}
          onChange={(date) => {
            setFieldValue('date', date).then(() => {
              validateField('date');
            });
          }}
          InputLabelProps={{ shrink: values.date !== '' }}
        />
      </div>
      {params.TASKLIST_REPORT_SHOW_TIMESPENT_FIELD === 'Y' ? (
        <>
          <div className="md:col-span-1 col-span-4">
            <Field
              type="number"
              name="timeSpent"
              as={InputUI}
              fullWidth
              label={<FormLabelUI text="Ráfordítás" />}
              helperText={<FormErrorUI message={errors.timeSpent} />}
              variant="standard"
              InputLabelProps={{ shrink: values.timeSpent && values.timeSpent !== '' }}
            />
          </div>
          <div className="col-span-4 md:col-span-1">
            <Field
              type="autocomplete"
              name="quantityUnitId"
              component={AutoCompleteSelectUI}
              fullWidth
              label={<FormLabelUI text="ME." />}
              variant="standard"
              helperText={<FormErrorUI message={errors.quantityUnitId} />}
              onChange={(_e, newVal) => {
                setFieldValue('quantityUnitId', newVal?.value ?? null).then(() => {
                  validateField('quantityUnitId');
                });
              }}
              InputLabelProps={{ shrink: values.quantityUnitId !== '' }}
              selectedValue={values.quantityUnitId}
              selectedLabelValue={values?.quantityUnit?.code}
              dataset={autocompleteOptions.qtyUnit}
              disabled
            />
          </div>
        </>
      ) : (
        <>
          <div className="col-span-4 md:col-span-1">
            <Field
              type="time"
              name="startTime"
              component={TimePickerUi}
              value={values.startTime}
              fullWidth
              label={<FormLabelUI text="Kezdés" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.startTime} />}
              onChange={(date) => {
                setFieldValue('startTime', date).then(() => {
                  validateField('startTime');
                });
              }}
              InputLabelProps={{ shrink: values.startTime !== '' }}
            />
          </div>
          <div className="col-span-4 md:col-span-1">
            <Field
              type="time"
              name="endTime"
              component={TimePickerUi}
              value={values.endTime}
              fullWidth
              label={<FormLabelUI text="Befejezés" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.endTime} />}
              onChange={(date) => {
                setFieldValue('endTime', date).then(() => {
                  validateField('endTime');
                });
              }}
              InputLabelProps={{ shrink: values.endTime !== '' }}
            />
          </div>
        </>
      )}
      <div className="col-span-4 md:col-span-2">
        <Field
          type="autocomplete"
          name="statusId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Státusz" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.statusId} />}
          onChange={(_e, newVal) => {
            setFieldValue('statusId', newVal?.value ?? null).then(() => {
              validateField('statusId');
            });
          }}
          InputLabelProps={{ shrink: values.statusId !== '' }}
          selectedValue={values.statusId}
          disabled={
            !values.taskListId || data?.responsiblePersonId ? data?.responsiblePersonId !== user?.userId : false
          }
          dataset={autocompleteOptions.status}
        />
      </div>
      <div className="md:col-span-2 col-span-4">
        <Field
          name="createdBy"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Lejelentő" />}
          variant="standard"
          value={user.nickName}
          disabled
        />
      </div>
    </div>
  );
};

export default FormFields;
